import React, { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, FolderOpen, Users, Coins, Mail, ClipboardList,
  Settings, ExternalLink, LogOut, Menu, X, Plus, ShoppingBag, Download, ShieldCheck
} from "lucide-react";
import { useAdminAuth } from "@/admin/AuthContext";
import { Logo } from "@/components/brand/Logo";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/events", label: "Events", icon: FolderOpen },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/credits", label: "Credits", icon: Coins },
  { to: "/admin/purchases", label: "Purchases", icon: ShoppingBag },
  { to: "/admin/downloads", label: "Downloads", icon: Download },
  { to: "/admin/security-events", label: "Security", icon: ShieldCheck },
  { to: "/admin/contacts", label: "Contact Messages", icon: Mail },
  { to: "/admin/hires", label: "Hire Us Requests", icon: ClipboardList },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

const PAGE_TITLES = {
  "/admin": "Dashboard",
  "/admin/events": "Events Management",
  "/admin/events/new": "Create Event",
  "/admin/customers": "Customers",
  "/admin/credits": "Credit Management",
  "/admin/purchases": "Purchase Records",
  "/admin/downloads": "Download Records",
  "/admin/security-events": "Security Events",
  "/admin/contacts": "Contact Messages",
  "/admin/hires": "Hire Us Requests",
  "/admin/settings": "Settings",
};

const titleFor = (path) => {
  if (PAGE_TITLES[path]) return PAGE_TITLES[path];
  if (path.startsWith("/admin/events/") && path.endsWith("/edit")) return "Edit Event";
  if (path.startsWith("/admin/events/") && path.endsWith("/folders")) return "Manage Event Folders";
  if (path.startsWith("/admin/events/") && path.endsWith("/pricing")) return "Event Pricing";
  if (path.includes("/folders/") && path.endsWith("/photos")) return "Photo Management";
  return "Admin";
};

const SidebarContent = ({ onNavigate }) => {
  const { user, logout } = useAdminAuth();
  return (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-7 pb-6 border-b border-white/5">
        <Link to="/admin" onClick={onNavigate} className="inline-flex items-center gap-3" data-testid="admin-sidebar-logo">
          <Logo size={42} linkTo={null} />
          <div>
            <p className="text-[10px] tracking-luxe uppercase text-white/40">Admin</p>
            <p className="font-display text-lg text-chrome leading-tight">Control Centre</p>
          </div>
        </Link>
      </div>

      <nav className="flex-1 px-4 py-6 overflow-y-auto">
        <ul className="space-y-1">
          {NAV.map((n) => (
            <li key={n.to}>
              <NavLink
                to={n.to}
                end={n.end}
                onClick={onNavigate}
                data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-body transition-all ${
                    isActive
                      ? "bg-gradient-to-r from-[#ff2e93]/15 to-[#18d3ff]/10 text-white chrome-border"
                      : "text-white/55 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <n.icon className="w-4 h-4 shrink-0" />
                <span className="truncate">{n.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="mt-8 pt-6 border-t border-white/5 space-y-1">
          <Link
            to="/"
            onClick={onNavigate}
            data-testid="admin-nav-back-to-website"
            className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-white/55 hover:text-white hover:bg-white/5 transition-colors"
          >
            <ExternalLink className="w-4 h-4 shrink-0" />
            Back to Website
          </Link>
          <button
            onClick={() => { logout(); onNavigate?.(); window.location.href = "/admin/login"; }}
            data-testid="admin-logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-white/55 hover:text-[#ff2e93] hover:bg-white/5 transition-colors text-left"
          >
            <LogOut className="w-4 h-4 shrink-0" />
            Logout
          </button>
        </div>
      </nav>

      {user && (
        <div className="px-5 py-4 border-t border-white/5 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full chrome-border bg-black/60 flex items-center justify-center text-white text-sm font-display">
            {(user.name || user.email)[0].toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-sm text-white truncate">{user.name || "Admin"}</p>
            <p className="text-[10px] tracking-luxe uppercase text-white/40 truncate">{user.email}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export const AdminShell = ({ children }) => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const title = titleFor(pathname);

  return (
    <div className="min-h-screen bg-[#040406] text-white relative">
      {/* Background decoration */}
      <div
        className="fixed inset-0 -z-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(40% 30% at 0% 0%, rgba(255,46,147,0.08), transparent 60%)," +
            "radial-gradient(40% 30% at 100% 100%, rgba(24,211,255,0.07), transparent 60%)," +
            "#040406",
        }}
      />

      {/* Sidebar — desktop */}
      <aside
        data-testid="admin-sidebar"
        className="hidden lg:flex fixed top-0 left-0 bottom-0 w-[260px] glass-strong border-r border-white/5 z-40"
      >
        <SidebarContent />
      </aside>

      {/* Sidebar — mobile drawer */}
      {open && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
            onClick={() => setOpen(false)}
            data-testid="admin-drawer-overlay"
          />
          <aside
            data-testid="admin-mobile-drawer"
            className="lg:hidden fixed top-0 left-0 bottom-0 w-[280px] max-w-[85vw] glass-strong border-r border-white/5 z-50 mm-panel"
          >
            <SidebarContent onNavigate={() => setOpen(false)} />
          </aside>
        </>
      )}

      {/* Main */}
      <div className="lg:ml-[260px] flex flex-col min-h-screen">
        {/* Top bar */}
        <header
          data-testid="admin-topbar"
          className="sticky top-0 z-30 glass-strong border-b border-white/5"
        >
          <div className="flex items-center gap-3 px-4 sm:px-6 py-3">
            <button
              onClick={() => setOpen(true)}
              className="lg:hidden w-9 h-9 rounded-full chrome-border bg-black/40 flex items-center justify-center text-white"
              data-testid="admin-mobile-menu-toggle"
              aria-label="Open admin menu"
            >
              <Menu className="w-4 h-4" />
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] tracking-luxe uppercase text-white/40">WelkomBiz · Admin</p>
              <h1 className="font-display text-xl sm:text-2xl text-chrome leading-tight truncate">{title}</h1>
            </div>
            <Link
              to="/admin/events/new"
              data-testid="admin-topbar-create-event"
              className="btn-primary !py-2 !px-4 !text-[10px] hidden sm:inline-flex"
            >
              <Plus className="w-3.5 h-3.5" /> Create Event
            </Link>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-10 py-6 sm:py-8 lg:py-10">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminShell;
